import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, LogIn, User, Truck, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LegalFooter from "@/components/LegalFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);

  const redirectTo = searchParams.get("redirect") || "/";

  // Auto-redirect already-logged-in users so they don't see a "blank-looking"
  // login form when they swipe back from the live tracking page. iPhone Safari
  // swipe-back would otherwise land on /login (briefly empty) before /bookings,
  // making it look like a broken intermediate page.
  useEffect(() => {
    if (user) {
      const target = (user.role === "driver" || user.role === "admin")
        ? (redirectTo.includes("/driver") ? redirectTo : "/driver")
        : redirectTo;
      navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Pre-fill the email with the last successfully-used address so iPhone users
  // (where Safari keychain autofill is unreliable inside installed PWAs) don't
  // have to retype it every time. We also set autocomplete="email" so native
  // browser autofill still works on regular browsers.
  const [formData, setFormData] = useState({
    email: (typeof window !== "undefined" && localStorage.getItem("link_last_email")) || "",
    password: ""
  });

  const isDriverLogin = redirectTo.includes("/driver");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      try { localStorage.setItem("link_last_email", formData.email); } catch { /* noop */ }
      toast.success(`Welcome back, ${user.preferred_name || user.first_name}!`);

      // Redirect based on role — admin should also land on the driver hub
      // (single-operator default). Customers go to their requested page.
      // `replace: true` keeps /login OUT of the history so swipe-back from
      // the driver dashboard doesn't flash this page on the way home.
      if (user.role === "driver" || user.role === "admin") {
        navigate(redirectTo.includes("/driver") ? redirectTo : "/driver", { replace: true });
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-5">
      <img
        src="/link-ride-logo.png"
        alt="Link Ride — Rideshare & Local Same Day Package Delivery"
        className="max-w-md w-full h-auto mb-6 rounded-xl shadow-[0_8px_40px_-12px_rgba(0,128,254,0.35)]"
        data-testid="login-logo"
      />
      <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="login-card">
        <CardHeader className="text-center">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="absolute left-4 top-4 text-zinc-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-16 h-16 rounded-full bg-[#0047FF]/10 flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-8 h-8 text-[#4D88FF]" />
          </div>
          <CardTitle className="text-2xl text-white">Welcome Back</CardTitle>
          <CardDescription className="text-zinc-400">
            {isDriverLogin ? "Sign in to your driver account" : "Sign in to your account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-zinc-400">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <button
              type="button"
              onClick={async () => {
                // Use window.prompt so the user EXPLICITLY confirms which
                // email they want reset — never silently reuse the form's
                // current email value (which is auto-filled with the LAST
                // logged-in account's address). That UX trap reset the
                // wrong account once and is what this prompt prevents.
                const suggested = (formData.email || "").trim();
                const target = window.prompt(
                  "Which account's password do you want to reset?\n\nType the EXACT email of the account that needs the reset link. We'll send a one-time link to that address.",
                  suggested
                );
                if (target === null) return; // cancelled
                const email = (target || "").trim();
                if (!email || !email.includes("@")) {
                  toast.error("Please enter a valid email address");
                  return;
                }
                try {
                  await axios.post(`${API}/auth/forgot-password`, { email });
                  toast.success(
                    `If an account exists for ${email}, a reset link is on its way to that inbox.`,
                    { duration: 8000 }
                  );
                } catch {
                  toast.error("Could not send reset link. Try again in a minute.");
                }
              }}
              className="w-full text-center text-xs text-zinc-400 hover:text-[#4D88FF] underline underline-offset-4 mt-1"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 space-y-3">
            <p className="text-center text-sm text-zinc-500">Don't have an account?</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800"
                onClick={() => navigate("/register/customer")}
                data-testid="register-customer-link"
              >
                <User className="w-4 h-4 mr-2" />
                Customer
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800"
                onClick={() => navigate("/driver/register")}
                data-testid="register-driver-link"
              >
                <Truck className="w-4 h-4 mr-2" />
                Driver
              </Button>
            </div>
          </div>

          {/* Franchise operator entry point. Franchisees log in with the
              admin credentials they set during onboarding — but until they
              apply, there's nowhere to go. Surface both paths here so
              prospective operators can find the door. */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <button
              type="button"
              onClick={() => navigate("/franchise")}
              className="w-full flex items-center justify-center gap-2 text-xs text-[#4D88FF] hover:text-[#7AADFF] font-semibold py-2 rounded-md hover:bg-[#0047FF]/5 transition-colors"
              data-testid="franchise-apply-link"
            >
              <Building2 className="w-3.5 h-3.5" />
              Own a franchise? Apply or check status
            </button>
            <p className="text-[10px] text-zinc-600 text-center mt-1 px-2 leading-relaxed">
              Approved franchise operators sign in above with the admin email set during onboarding.
            </p>
          </div>
        </CardContent>
      </Card>
      <LegalFooter />
    </div>
  );
}
